import { FloatingActionButton } from '@trucktrax/trucktrax-common';
import { UserDto, VoidFunc } from '@trucktrax/trucktrax-ts-common';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  ADD_EMAIL_NOTIFICATION_MODAL_TITLE,
  ADMIN_KEYS,
  CUSTOM_COMPONENT,
  TEXT_DISPLAY,
} from '../../../constants/appConstants';
import {
  createSubscription,
  deleteSubscription,
  PlantOption,
} from '../../../services/emailSubscriptionsService';
import { getUserByUrl } from '../../../services/usersService';
import { EmailSubscriptionGroup } from '../../../types';
import { userDisplayName } from '../../../util/userUtil';
import { noop } from '../../../util/appUtil';
import {
  cellsToSubscriptions,
  diffSubscriptions,
  subscriptionCellKeys,
} from '../../../util/emailNotificationUtil';
import { devErrorAndLog } from '../../../util/errorUtil';
import { checkEmailAddress } from '../../../util/validation';
import AddFeatureModal from '../../shared/AddFeatureModal';
import AdminAddModal, { AdminConfig } from '../../shared/admin/AdminAddModal';
import EmailRecipientField, { EmailSuggestion } from './EmailRecipientField';
import NotificationsField, { notificationCells } from './NotificationsField';
import styles from './emailNotifications.module.css';

export const NOTIFICATIONS_FIELD_KEY = 'notifications';

export const NOTHING_SELECTED_MESSAGE = 'Select at least one notification for a plant.';

export const EDIT_MODAL_TITLE = 'EDIT EMAIL NOTIFICATIONS';
export const SAVE_BUTTON_TEXT = 'Save';

export const NOTIFICATIONS_LABEL = 'Notifications';
export const RECIPIENT_LABEL = 'User';

export const PLANT_DISPATCH_NOTE = 'Every plant dispatches unique emails.';

/**
 * Configure the notifications one address receives.
 *
 * The API stores flat rows -- one per (email, notification type, plant) -- but an
 * admin thinks in addresses, so the modal collects one address and every notification
 * it receives, then writes the difference against what the address already holds. See
 * `cellsToSubscriptions` and `diffSubscriptions`.
 *
 * An AdminAddModal like the other add modals, so the per-field customValidation
 * collected into keyed errors, the discard prompt shown only when something
 * actually changed, and the submit contract (value, errorCallback, successCallback)
 * all come for free. The FAB lives here too, as it does in Trucks, Drivers, Plants,
 * Devices, Regions and Checklists.
 *
 * Notifications are set a topic at a time: a tab per topic, and under it the plants
 * grouped by region with a checkbox each. That is the shape of the contract -- one
 * ticked box is one stored row. The two dropdowns it replaced could only express a full
 * cross-product of the topics chosen against the plants chosen, so an address wanting
 * Incoming Materials at one plant and Diversions at another could not be expressed
 * even though the rows behind it were fine. See {@link NotificationsField}.
 *
 * One modal serves creating and editing -- a row click opens it over the address
 * with everything prefilled and the submit reading Save. Prefill goes through each
 * field's initialSelected, which is what AdminAddModal's initializeValues reads,
 * and that only runs on mount, so the modal is keyed by address to re-initialise
 * when a different row is opened.
 */
export class EmailNotificationsAddModal extends Component<
  EmailNotificationsAddModalProps,
  EmailNotificationsAddModalState
> {
  static defaultProps: Partial<EmailNotificationsAddModalProps> = {
    plantOptions: [],
    createSubscription,
    deleteSubscription,
    getUserByUrl,
  };

  state: EmailNotificationsAddModalState = { email: '' };

  componentDidMount() {
    this.resetEmail();
  }

  componentDidUpdate(prevProps: EmailNotificationsAddModalProps) {
    // The modal is remounted per address, but the open/close cycle for one address is
    // not, so a discarded edit would otherwise decide whether the rest of the form
    // shows.
    if (!prevProps.isOpen && this.props.isOpen) {
      this.resetEmail();
    }
  }

  resetEmail = () => {
    this.setState({ email: this.props.group?.email ?? '' });

    /*
     * When editing there is no recipient field to resolve the address, so the narrowing
     * is seeded here instead. Reuses the same generation-guarded lookup the field
     * triggers, rather than a second path to the same answer.
     */
    const userUrl = this.props.recipientUser?.url;
    if (this.props.group && userUrl) {
      this.onUserChange({ email: this.props.group.email, userUrl });
    }
  };

  /**
   * How a locked recipient reads: their name, or the address when it belongs to no user.
   * The same fallback the table's User column uses, so the two agree.
   */
  recipientLabel = () =>
    userDisplayName(this.props.recipientUser) || (this.props.group?.email ?? '');

  /** Mirrors the address so {@link hasRecipient} can follow it. */
  onChangeHandler = (value: any) => {
    const email = value?.[ADMIN_KEYS.EMAIL] ?? '';
    if (email !== this.state.email) {
      this.setState({ email });
    }
  };

  /**
   * Whether there is a recipient to configure notifications for.
   *
   * Gated on the address validating, not merely being non-empty: a half-typed address
   * is not a recipient, and revealing the plant list against one would offer a choice
   * that cannot be saved. `checkEmailAddress` is the same check the field itself
   * applies, so the form cannot disagree with itself about what counts.
   */
  hasRecipient = () => checkEmailAddress(this.state.email) === '';

  /**
   * Bumped per lookup so a slow reply for an address the admin has since changed
   * cannot land last and narrow the tabs to the wrong recipient.
   */
  private userLookup = 0;

  /** Opened over a row, which is what locks the recipient. */
  isEditing = () => !!this.props.group;

  /**
   * What the recipient already receives, whether the modal was opened over their row or
   * the admin typed an address that turns out to already have notifications.
   *
   * That second case is the point. Without it an Add against an existing address could
   * not see its rows, so ticking a plant it was already subscribed to POSTed a
   * duplicate and the API's unique index answered 409 behind a generic toast -- and
   * DEV-3769 requires duplicates be prevented. Resolving the address to its existing
   * subscriptions makes Add and Edit the same operation, so a duplicate is impossible
   * by construction rather than caught by a guard.
   *
   * The recipient field stays unlocked either way: `isEditing` is about how the modal
   * was opened, not about what the address turns out to be.
   */
  existingGroup = (): EmailSubscriptionGroup | undefined =>
    this.props.group ?? this.props.findGroupForEmail?.(this.state.email);

  /**
   * Resolves the regions the chosen recipient belongs to, which the notifications
   * field narrows its plant list to.
   *
   * The autocomplete carries no regions -- `UserSlimDto` is
   * `(DisplayName, Email, UserUrl)` -- so the user record is read for them. An address
   * belonging to no user leaves the tabs unnarrowed, which is the supported case for a
   * shared inbox or a distribution alias.
   *
   * A failed read also leaves them unnarrowed rather than emptying the list: not
   * knowing a recipient's regions is a reason to offer all of them, not none.
   */
  onUserChange = async (user?: EmailSuggestion) => {
    this.userLookup += 1;
    const lookup = this.userLookup;

    if (!user?.userUrl) {
      this.setState({ userRegionUrls: undefined });
      return;
    }

    try {
      const record = await this.props.getUserByUrl!(user.userUrl);
      const urls = (record?.regions ?? [])
        .map((region: { url?: string }) => region?.url)
        .filter((url: string | undefined): url is string => !!url);

      if (lookup === this.userLookup) {
        // An empty list is treated as unknown rather than as "no regions": a form with
        // no tabs and no explanation is worse than one offering too many.
        this.setState({ userRegionUrls: urls.length > 0 ? urls : undefined });
      }
    } catch {
      if (lookup === this.userLookup) {
        this.setState({ userRegionUrls: undefined });
      }
    }
  };

  /**
   * The regions the notifications field may offer.
   *
   * The recipient's own regions, plus any the address already has rows in. That second
   * part is not politeness: without it, an address holding rows in a region its owner
   * has since left would show no tab for them, so they would keep sending with nothing
   * in the screen able to show or untick them -- and the save guard would quietly
   * preserve them, since a row with no cell is withheld from the diff.
   */
  allowedRegionUrls = () => {
    const { userRegionUrls } = this.state;
    if (!userRegionUrls) {
      return undefined;
    }

    const subscribed = (this.existingGroup()?.regions ?? [])
      .map(region => region.url)
      .filter((url): url is string => !!url);

    return Array.from(new Set([...userRegionUrls, ...subscribed]));
  };

  /** The cells to tick for the address being edited. */
  initialCellKeys = () => subscriptionCellKeys(this.existingGroup()?.subscriptions ?? []);

  /**
   * The plants a save may write, which is the plants the field is currently offering.
   *
   * Not simply every plant. The field narrows its list to the recipient's regions but
   * keeps the whole selection, hidden keys included -- deliberately, so switching
   * recipient mid-edit does not silently discard ticks. The consequence is that a tick
   * made before the narrowing would otherwise still reach the API, creating a
   * subscription in a region the chosen recipient has nothing to do with, which is the
   * one thing the narrowing exists to prevent.
   *
   * Intersecting here rather than pruning the selection keeps the two concerns apart:
   * the field decides what is shown, the save decides what is written.
   *
   * Existing rows are never lost to this: `allowedRegionUrls` includes the regions the
   * address is already subscribed in, so every stored row stays representable.
   */
  writablePlantOptions = () => {
    const allowed = this.allowedRegionUrls();
    const options = this.props.plantOptions ?? [];
    return allowed ? options.filter(option => allowed.includes(option.region.url ?? '')) : options;
  };

  /** Every cell a save may write, which is what it maps ticks back to rows through. */
  cells = () => notificationCells(this.writablePlantOptions());

  /**
   * The address's rows that the field can represent, which is what a save may delete.
   *
   * A row whose plant is missing, or whose plant is no longer in the plant list -- an
   * archived one, say -- has no cell to be ticked in, so it would read as unticked and
   * be deleted by a save that never showed it. Withheld from the diff instead, and so
   * left exactly as it is.
   */
  diffableSubscriptions = () => {
    const known = new Set((this.props.plantOptions ?? []).map(option => option.key));
    return (this.existingGroup()?.subscriptions ?? []).filter(
      subscription => !!subscription.plant?.url && known.has(subscription.plant.url),
    );
  };

  config = (): AdminConfig[] => {
    const { plantOptions, regionNames, group } = this.props;

    const fields: AdminConfig[] = [
      this.isEditing()
        ? {
            /*
             * Plain text, not a disabled input. A greyed-out box still reads as a
             * control -- it looks like something that could be edited under some
             * condition, and there is no such condition: the address is what every row
             * for this recipient is keyed on, so changing it would mean deleting the
             * whole set and recreating it under another one.
             *
             * `value` here overrides the one AdminAddModal computes, because its
             * `{...config}` spread comes after. So the name is what shows while the
             * field's stored value stays the address the submit sends.
             *
             * No customValidation: the address is not editable here, so a validation
             * failure could only produce an inline error this field cannot render and a
             * Save that never succeeds.
             */
            type: TEXT_DISPLAY,
            key: ADMIN_KEYS.EMAIL,
            label: RECIPIENT_LABEL,
            className: 'fullWidthSpace',
            value: this.recipientLabel(),
            // Bold, so it reads as the recipient rather than as placeholder text now
            // that it is not in a box. Matches the table's User column, which is bold
            // for the same reason.
            textClassName: 'txt-bold',
            initialSelected: { value: group?.email ?? '' },
            dataTest: 'add-email-notification-user',
          }
        : {
            type: CUSTOM_COMPONENT,
            key: ADMIN_KEYS.EMAIL,
            label: RECIPIENT_LABEL,
            component: EmailRecipientField,
            className: 'fullWidthSpace',
            isRequired: true,
            customValidation: checkEmailAddress,
            initialSelected: { value: group?.email ?? '' },
            // Reported alongside the value, so the notifications field can narrow its
            // regions to the recipient's.
            onUserChange: this.onUserChange,
            dataTest: 'add-email-notification-email',
            errorDataTest: 'add-email-notification-email-error',
          },
    ];

    /*
     * Nothing below the recipient until there is one. Which plants are even offered
     * depends on who is receiving -- the regions are narrowed to theirs -- so a plant
     * list shown first would be answering a question the form has not asked yet, and
     * would then have to change under the admin once it had.
     */
    if (!this.hasRecipient()) {
      return fields;
    }

    fields.push({
      type: CUSTOM_COMPONENT,
      key: NOTIFICATIONS_FIELD_KEY,
      label: NOTIFICATIONS_LABEL,
      component: NotificationsField,
      className: 'fullWidthSpace',
      isRequired: true,
      plantOptions,
      regionNames,
      allowedRegionUrls: this.allowedRegionUrls(),
      initialSelected: { value: this.initialCellKeys() },
      // An address with nothing ticked has no rows to write, so it is refused
      // rather than saved as a no-op.
      customValidation: (value: string[]) =>
        (value ?? []).length > 0 ? '' : NOTHING_SELECTED_MESSAGE,
      dataTest: 'add-email-notification-notifications',
    });

    return fields;
  };

  onSubmit = async (dto: any, onError: any, buttonCallback: any) => {
    const email = (dto?.[ADMIN_KEYS.EMAIL] ?? '').trim();

    const desired = cellsToSubscriptions(email, dto?.[NOTIFICATIONS_FIELD_KEY] ?? [], this.cells());

    // Every cell the field showed: one it no longer holds is one the admin unticked.
    const { toCreate, toDelete } = diffSubscriptions(desired, this.diffableSubscriptions());

    try {
      // Deletes first: a row being replaced by one that differs only in a field the
      // uniqueness index covers would otherwise 409 against the row on its way out.
      await Promise.all(
        toDelete
          .filter(subscription => subscription.id != null)
          .map(subscription => this.props.deleteSubscription!(subscription.id!)),
      );
      await Promise.all(toCreate.map(subscription => this.props.createSubscription!(subscription)));
      this.props.onSaved();
    } catch (e: any) {
      buttonCallback({});
      this.props.devErrorAndLog!(
        `Unable to save the notifications for “${email}”.`,
        `Add Email Notification: ${e?.toString()}`,
      );
    }
  };

  render() {
    return (
      <div>
        <FloatingActionButton onClick={this.props.openModal} />
        <AddFeatureModal
          title={this.isEditing() ? EDIT_MODAL_TITLE : ADD_EMAIL_NOTIFICATION_MODAL_TITLE}
          isOpen={this.props.isOpen}
          onCancel={this.props.closeModal}
          style={styles.addEmailNotificationModal}
        >
          <AdminAddModal
            /*
              Keyed by address. AdminAddModal reads initialSelected only on mount, so
              opening a different row has to remount it -- otherwise the fields show
              the address they were first initialised from.
            */
            key={this.existingGroup()?.key ?? 'new'}
            config={this.config()}
            // Save either way: adding an address and editing one are the same act
            // from here, and "Add Email" described a form that asked only for an
            // address.
            addButtonText={SAVE_BUTTON_TEXT}
            addButtonDataTest="add-email-notification-button"
            onRightBtnClick={this.onSubmit}
            onChangeHandler={this.onChangeHandler}
            discardAcceptAction={this.props.discardAcceptAction}
            discardRejectAction={this.props.discardRejectAction}
            isCloseRequest={this.props.isCloseRequest}
          >
            {this.isEditing() ? (
              <p className={styles.modalNote} data-test="email-notification-plant-note">
                {PLANT_DISPATCH_NOTE}
              </p>
            ) : null}
          </AdminAddModal>
        </AddFeatureModal>
      </div>
    );
  }
}

export interface EmailNotificationsAddModalState {
  /**
   * The regions the chosen recipient belongs to. Absent means the recipient is unknown
   * -- or has no regions on record -- and every region is offered.
   */
  userRegionUrls?: string[];
  /**
   * The address as it currently stands, mirrored out of AdminAddModal so the modal can
   * tell whether to show the notifications field at all. AdminAddModal owns its value
   * and only reports it through onChangeHandler, so this is the only way to see it.
   */
  email: string;
}

export interface EmailNotificationsAddModalProps {
  isOpen: boolean;
  isCloseRequest: boolean;
  openModal: VoidFunc;
  closeModal: VoidFunc;
  discardAcceptAction: typeof noop;
  discardRejectAction: typeof noop;
  /** The address being edited. Absent when the modal was opened by the add button. */
  group?: EmailSubscriptionGroup;
  /**
   * Resolves a typed address to the notifications it already has, so an Add against an
   * existing recipient prefills rather than colliding with the unique index.
   */
  findGroupForEmail?: (email: string) => EmailSubscriptionGroup | undefined;
  /**
   * The account the edited address belongs to, so a locked recipient can be named
   * rather than shown as an address. Absent for an address belonging to no user.
   */
  recipientUser?: UserDto;
  plantOptions?: PlantOption[];
  /** Region url to display name, for the dropdown group headers. */
  regionNames: { [url: string]: string };
  /** Called once a create or save lands, so the caller can re-read the table. */
  onSaved: () => void;
  devErrorAndLog?: typeof devErrorAndLog;
  createSubscription?: typeof createSubscription;
  deleteSubscription?: typeof deleteSubscription;
  getUserByUrl?: typeof getUserByUrl;
}

export default connect<any, any, any>(null, { devErrorAndLog })(EmailNotificationsAddModal);
